class ArcticHeroVideo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.currentIndex = 0;
    this.timeoutId = null;
    this.pauseReasons = new Set();

    this.slideNodes = Array.from(this.querySelectorAll('.arctic-hero__slide'));
    this.dotsContainer = this.querySelector('.arctic-hero__dots');
    this.autoplayDelay = Number.parseInt(this.dataset.autoplay, 10) || 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayEnabled = this.autoplayDelay > 0 && this.slideNodes.length > 1 && !this.reducedMotion;

    this.handleMouseEnter = () => this.pauseAutoplay('hover');
    this.handleMouseLeave = () => this.resumeAutoplay('hover');
    this.handleFocusIn = () => this.pauseAutoplay('focus');
    this.handleFocusOut = () => this.resumeAutoplay('focus');

    this.buildDots();
    this.bindPauseEvents();
    this.goToSlide(0);
    this.removeAttribute('data-media-loading');
    this.setAttribute('data-ready', '');

    if (this.autoplayEnabled) {
      this.startAutoplay();
    } else {
      this.setAttribute('data-paused', '');
    }
  }

  disconnectedCallback() {
    this.stopAutoplay({ markPaused: false });
    this.unbindPauseEvents();
  }

  buildDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    this.dotNodes = this.slideNodes.map((slide, index) => {
      const dot = document.createElement('button');
      const heading = slide.querySelector('.arctic-hero__heading')?.textContent?.trim();

      dot.type = 'button';
      dot.className = 'arctic-hero__dot arctic-dot';
      dot.setAttribute('aria-label', heading ? `Show slide: ${heading}` : `Show slide ${index + 1}`);

      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.restartAutoplay();
      });

      this.dotsContainer.append(dot);
      return dot;
    });
  }

  bindPauseEvents() {
    if (!this.autoplayEnabled) return;

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
  }

  unbindPauseEvents() {
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
  }

  goToSlide(index, options = {}) {
    if (!this.slideNodes.length) return;

    const animateText = options.animateText !== false;
    const nextIndex = (index + this.slideNodes.length) % this.slideNodes.length;

    if (nextIndex !== this.currentIndex) {
      this.resetTextForIndex(this.currentIndex);
    }

    this.currentIndex = nextIndex;

    this.slideNodes.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;

      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dotNodes?.forEach((dot, dotIndex) => {
      const isActive = dotIndex === nextIndex;

      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.playActiveVideo();

    if (animateText) {
      this.animateTextForIndex(nextIndex);
    }
  }

  animateTextForIndex(index) {
    const activeSlide = this.slideNodes[index];
    const animatedElements = activeSlide?.querySelectorAll('motion-element');

    animatedElements?.forEach((element) => {
      if (typeof element.refreshAnimation === 'function') {
        element.refreshAnimation();
      }
    });
  }

  resetTextForIndex(index) {
    const previousSlide = this.slideNodes[index];
    const animatedElements = previousSlide?.querySelectorAll('motion-element[data-text]');

    animatedElements?.forEach((element) => {
      if (typeof element.resetAnimation === 'function') {
        element.resetAnimation();
      }
    });
  }

  playActiveVideo() {
    this.slideNodes.forEach((slide, index) => {
      const video = slide.querySelector('video');

      if (!video) return;

      if (index === this.currentIndex && !this.reducedMotion) {
        try {
          video.currentTime = 0;
        } catch (error) {
          // Some browsers reject currentTime changes before enough media is loaded.
        }

        video.play().catch(() => {});
        return;
      }

      video.pause();

      try {
        video.currentTime = 0;
      } catch (error) {
        // Some browsers reject currentTime changes before enough media is loaded.
      }
    });
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.pauseReasons.size > 0) return;

    this.stopAutoplay({ markPaused: false });
    this.removeAttribute('data-paused');

    this.timeoutId = window.setTimeout(() => {
      this.goToSlide(this.currentIndex + 1);
      this.startAutoplay();
    }, this.autoplayDelay);
  }

  restartAutoplay() {
    this.stopAutoplay({ markPaused: false });

    if (this.autoplayEnabled && this.pauseReasons.size === 0) {
      this.startAutoplay();
      return;
    }
  }

  pauseAutoplay(reason) {
    if (!this.autoplayEnabled) return;

    this.pauseReasons.add(reason);
    this.stopAutoplay({ markPaused: true });
  }

  resumeAutoplay(reason) {
    if (!this.autoplayEnabled) return;

    this.pauseReasons.delete(reason);

    if (this.pauseReasons.size === 0) {
      this.startAutoplay();
    }
  }

  stopAutoplay(options = {}) {
    const markPaused = options.markPaused !== false;

    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (markPaused) {
      this.setAttribute('data-paused', '');
    }
  }
}

if (!customElements.get('arctic-hero-video')) {
  customElements.define('arctic-hero-video', ArcticHeroVideo);
}
